import { useNavigate } from "@tanstack/react-router";
import type { FC, SVGProps } from "react";

import {
  BankIcon,
  StorefrontStoreMarketplaceIcon,
  UploadIcon,
  UserIcon,
} from "@/assets/icons";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import { useAudiolab } from "@/components/providers/AudiolabProvider/hooks";
import { useAlbumsControllerAlbumsList } from "@/lib/lutely-api/gen/albums/albums";
import { useStripeControllerGetStripeAccountCapabilities } from "@/lib/lutely-api/gen/stripe/stripe";
import {
  useTracksControllerTracksList,
  useTracksControllerUploadCount,
} from "@/lib/lutely-api/gen/tracks/tracks";

export type GetStartedStepKey = "profile" | "upload" | "payout" | "publish";

export type GetStartedStep = {
  key: GetStartedStepKey;
  label: string;
  Icon: FC<SVGProps<SVGSVGElement>>;
  complete: boolean;
  onClick?: () => void;
};

export type GetStartedMilestones = {
  steps: GetStartedStep[];
  completedCount: number;
  total: number;
  isLoading: boolean;
  allComplete: boolean;
};

/**
 * Derives the four artist onboarding milestones entirely from existing
 * endpoints — nothing new is persisted. Each `complete` flag reads the same
 * signal the rest of the app uses so the tracker can never drift from reality:
 * the "publishable profile" rule from ArtistDashboardHeader, the all-time
 * upload count, Stripe Connect onboarding, and any published track or album.
 *
 * Pass `enabled: false` for non-artists so the milestone queries never fire.
 */
export function useGetStartedMilestones({
  enabled = true,
}: { enabled?: boolean } = {}): GetStartedMilestones {
  const navigate = useNavigate();
  const { isLoading: userLoading } = useLoggedInUser();
  // The artist the rest of Audiolab is pointed at — the context falls back to
  // the default creator, so this is never less defined than `defaultCreator`.
  // Every step has to describe the same artist the Create menu would act on,
  // or a label account switching artists would see one artist's progress and
  // create releases under another's.
  const { selectedCreator, allCreators } = useAudiolab();
  const creatorId = selectedCreator?.creatorId;

  // The provider holds the creator object as it was when the artist was picked
  // and never re-seats it, so an avatar upload or a profile save — which
  // refetch the user query — leave that snapshot stale. `allCreators` is the
  // refetched list, so the live record is the one to read fields off.
  const creator =
    allCreators.find((c) => c.creatorId === creatorId) ?? selectedCreator;

  // 1. Complete profile — the same rule the dashboard uses to decide a profile
  //    is publishable (ArtistDashboardHeader: name, handle and bio).
  const profileComplete = !!(
    creator?.stageName &&
    creator?.slug &&
    creator?.bio &&
    creator?.profilePictureUrl
  );

  // 2. Upload a track — all-time count for the user's creators.
  const { data: uploadCount, isLoading: uploadLoading } =
    useTracksControllerUploadCount({ query: { enabled } });
  const hasUploaded = (uploadCount ?? 0) > 0;

  // 3. Connect payout — Stripe Connect onboarding finished.
  const { data: capabilities, isLoading: payoutLoading } =
    useStripeControllerGetStripeAccountCapabilities({ query: { enabled } });
  const payoutConnected = capabilities?.onboardingComplete ?? false;

  // 4. Publish release — at least one published track or album.
  const { data: publishedTracks, isLoading: tracksLoading } =
    useTracksControllerTracksList(
      { creatorId, published: true, pageSize: 1 },
      { query: { enabled: enabled && !!creatorId } },
    );
  const { data: publishedAlbums, isLoading: albumsLoading } =
    useAlbumsControllerAlbumsList(
      { creatorId, published: true, pageSize: 1 },
      { query: { enabled: enabled && !!creatorId } },
    );
  const hasPublished =
    (publishedTracks?.content?.length ?? 0) > 0 ||
    (publishedAlbums?.content?.length ?? 0) > 0;

  const steps: GetStartedStep[] = [
    {
      key: "profile",
      label: "Complete profile",
      Icon: UserIcon,
      complete: profileComplete,
      onClick: () =>
        navigate({
          to: "/audiolab",
          search: (prev) => ({ ...prev, edit: "profile" as const }),
        }),
    },
    {
      key: "upload",
      label: "Upload track",
      Icon: UploadIcon,
      complete: hasUploaded,
    },
    {
      key: "payout",
      label: "Connect payout",
      Icon: BankIcon,
      complete: payoutConnected,
      onClick: () =>
        navigate({ to: "/audiolab/sales", search: { setup: "payout" } }),
    },
    {
      key: "publish",
      label: "Publish release",
      Icon: StorefrontStoreMarketplaceIcon,
      complete: hasPublished,
      // No onClick: like `upload`, the row is rendered as the trigger for the
      // New release chooser, which asks Single or Album before creating.
    },
  ];

  const completedCount = steps.filter((step) => step.complete).length;
  const allComplete = completedCount === steps.length;

  // Gate only on the first load of each query — once loaded, React Query keeps
  // `isLoading` false during background refetches, so the card never blanks.
  // The publish queries only load when a creator exists to scope them.
  const publishLoading = !!creatorId && (tracksLoading || albumsLoading);
  const isLoading =
    userLoading || uploadLoading || payoutLoading || publishLoading;

  return {
    steps,
    completedCount,
    total: steps.length,
    isLoading,
    allComplete,
  };
}
